import React, { useCallback, useMemo } from 'react';
import {
  TabBar,
  TabBarProps,
  TabDescriptor,
} from 'react-native-tab-view';
import { styled, SxProp, useSx } from 'dripsy';
import type { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import type {
  ParamListBase,
  TabNavigationState,
} from '@react-navigation/native';

import { useColors } from '@hooks';
import { BaseText } from '../../components/common/BaseText';
import { IS_WEB } from '@utils';

export const TOP_TAB_BAR_HEIGHT = 50;

const indicatorStyle: SxProp = {
  bg: '$primary300',
  borderRadius: 4,
  height: 4,
};

// Using styled with TabBar causes type issues
const style: SxProp = {
  bg: '$background0',
  height: TOP_TAB_BAR_HEIGHT,
  elevation: 0,
  shadowRadius: 0,
  shadowOffset: {
    height: 0,
    width: 0,
  },
  borderBottomColor: '$background30',
  borderBottomWidth: 1,
};

interface ILabelStyleProps {
  color: string;
}

const Label = styled(BaseText)((props: ILabelStyleProps) => ({
  color: props.color,
  variant: 'text.medium',
}));

type NavigationRouteType =
  TabNavigationState<ParamListBase>['routes'][number];

interface IRenderLabelParams {
  route: NavigationRouteType;
  focused: boolean;
  color: string;
}

export function TopTabBar(props: MaterialTopTabBarProps) {
  const sx = useSx();
  const colors = useColors();
  const { state, navigation, descriptors } = props;

  const renderLabel = useCallback(
    ({ route, color }: IRenderLabelParams) => {
      const title = descriptors[route.key].options.title;

      return <Label color={color}>{title}</Label>;
    },
    [],
  );

  const onTabPress = useCallback<
    Required<TabBarProps<any>>['onTabPress']
  >(({ route }) => {
    if (IS_WEB) {
      // On web navigate only happens after tab switch transition,
      // to prevent this behavior we navigate manually.
      navigation.navigate(route.name, route.params);
    }
  }, []);

  const options = useMemo(() => {
    return state.routes.reduce<
      Record<string, TabDescriptor<NavigationRouteType>>
    >((acc, route) => {
      const routeOptions = acc[route.key] ?? {};
      acc[route.key] = {
        ...routeOptions,
        label: renderLabel,
      };

      return acc;
    }, {});
  }, []);

  return (
    <TabBar
      {...props}
      style={sx(style)}
      navigationState={state}
      activeColor={colors.$primary300}
      inactiveColor={colors.$text400}
      indicatorStyle={sx(indicatorStyle)}
      onTabPress={onTabPress}
      options={options}
    />
  );
}
